import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';

interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
}

interface AdminDashboardProps {
  user: User;
  getToken: () => Promise<string>;
  onBack: () => void;
}

interface ActiveCheckout {
  id: string;
  user_id: string;
  checked_out_at: string;
  users: { id: string; name: string; email: string; phone?: string };
  carts: { id: string; cart_number: number; qr_code: string };
}

interface AuditLog {
  id: string;
  action: string;
  created_at: string;
  details: any;
  users: { name: string; email: string } | null;
  carts: { cart_number: number } | null;
}

interface Cart {
  id: string;
  cart_number: number;
  qr_code: string;
  status: string;
  seats?: number;
  color?: string;
  description?: string;
  qr_image_url?: string;
  cart_images?: string[];
  out_of_service?: boolean;
  created_at: string;
}

interface AdminUser {
  id: string;
  auth_id: string;
  email: string;
  name: string;
  phone?: string;
  isAdmin: boolean;
  status: string;
  createdAt: string;
}

interface Invitation {
  id: string;
  email: string;
  name: string;
  phone?: string;
  is_admin: boolean;
  invited_by: string;
  invited_at: string;
  expires_at: string;
  status: string;
  inviter?: {
    name: string;
    email: string;
  };
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

type TabType = 'checkouts' | 'carts' | 'users' | 'logs' | 'stats' | 'config';

function AdminDashboard({ user, getToken, onBack }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('checkouts');
  const [activeCheckouts, setActiveCheckouts] = useState<ActiveCheckout[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [carts, setCarts] = useState<Cart[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Cart management state
  const [showCartModal, setShowCartModal] = useState(false);
  const [editingCart, setEditingCart] = useState<Cart | null>(null);
  const [cartFormData, setCartFormData] = useState({ 
    cart_number: '', 
    qr_code: '',
    seats: '',
    color: '',
    description: ''
  });

  // User management state (now invitation-based)
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [userFormData, setUserFormData] = useState({ 
    name: '', 
    email: '',
    phone: '',
    isAdmin: false 
  });
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetUserId, setResetUserId] = useState('');

  // Bulk invite state
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  const [bulkInviteFile, setBulkInviteFile] = useState<File | null>(null);
  const [bulkInviteLoading, setBulkInviteLoading] = useState(false);
  const [bulkInviteResults, setBulkInviteResults] = useState<any>(null);

  // Sorting state
  const [userSortField, setUserSortField] = useState<'name' | 'email' | 'createdAt'>('createdAt');
  const [userSortDirection, setUserSortDirection] = useState<'asc' | 'desc'>('desc');
  const [logSortDirection, setLogSortDirection] = useState<'asc' | 'desc'>('desc');

  // Search state
  const [cartSearch, setCartSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Admin transfer state
  const [showAdminTransfer, setShowAdminTransfer] = useState(false);
  const [adminTransferCheckoutId, setAdminTransferCheckoutId] = useState('');
  const [adminTransferCartId, setAdminTransferCartId] = useState('');
  const [adminTransferCurrentUserId, setAdminTransferCurrentUserId] = useState('');
  const [adminTransferUsers, setAdminTransferUsers] = useState<any[]>([]);
  const [adminTransferSearch, setAdminTransferSearch] = useState('');
  const [selectedTransferUser, setSelectedTransferUser] = useState<any>(null);

  // Admin checkout state
  const [showAdminCheckout, setShowAdminCheckout] = useState(false);
  const [adminCheckoutCartId, setAdminCheckoutCartId] = useState('');
  const [adminCheckoutUsers, setAdminCheckoutUsers] = useState<any[]>([]);
  const [adminCheckoutSearch, setAdminCheckoutSearch] = useState('');
  const [selectedCheckoutUser, setSelectedCheckoutUser] = useState<any>(null);

  // Config state
  const [configLogo, setConfigLogo] = useState('');
  const [configTagline, setConfigTagline] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Image management state
  const [showQRModal, setShowQRModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState<Cart | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploadingQR, setUploadingQR] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'checkouts') {
        await fetchCheckouts();
      } else if (activeTab === 'carts') {
        await fetchCarts();
      } else if (activeTab === 'users') {
        await fetchUsers();
        await fetchPendingInvitations();
      } else if (activeTab === 'logs') {
        await fetchLogs();
      } else if (activeTab === 'stats') {
        await fetchStatistics();
      } else if (activeTab === 'config') {
        await fetchConfig();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCheckouts = async () => {
    const token = await getToken();
    const response = await fetch(`${API_URL}/api/admin/checkouts/active`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setActiveCheckouts(data);
    }
  };

  const fetchLogs = async () => {
    const token = await getToken();
    const response = await fetch(`${API_URL}/api/admin/audit-logs`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setAuditLogs(data);
    }
  };

  const fetchCarts = async () => {
    const token = await getToken();
    const response = await fetch(`${API_URL}/api/carts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setCarts(data);
    }
  };

  const fetchUsers = async () => {
    const token = await getToken();
    const response = await fetch(`${API_URL}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      const usersData = data.map((u: any) => ({
        ...u,
        isAdmin: u.is_admin,
        createdAt: u.created_at
      }));
      setUsers(usersData);
    }
  };

  const fetchPendingInvitations = async () => {
    const token = await getToken();
    const response = await fetch(`${API_URL}/api/admin/invitations`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setPendingInvitations(data);
    }
  };

  const fetchStatistics = async () => {
    const token = await getToken();
    const response = await fetch(`${API_URL}/api/admin/statistics`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setStatistics(data);
    }
  };

  const fetchConfig = async () => {
    const response = await fetch(`${API_URL}/api/config`);
    if (response.ok) {
      const data = await response.json();
      setConfigLogo(data.logo || '');
      setConfigTagline(data.tagline || '');
      setLogoPreview(data.logo || '');
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfigSave = async () => {
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('tagline', configTagline);
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await fetch(`${API_URL}/api/admin/config`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        alert('Configuration saved successfully!');
        setLogoFile(null);
        await fetchConfig();
      } else {
        alert('Failed to save configuration');
      }
    } catch (error) {
      alert('Error saving configuration');
    }
  };

  // ========== CART MANAGEMENT ==========

  const openCartModal = (cart?: Cart) => {
    if (cart) {
      setEditingCart(cart);
      setCartFormData({
        cart_number: cart.cart_number.toString(),
        qr_code: cart.qr_code,
        seats: cart.seats?.toString() || '',
        color: cart.color || '',
        description: cart.description || ''
      });
    } else {
      setEditingCart(null);
      setCartFormData({ cart_number: '', qr_code: '', seats: '', color: '', description: '' });
    }
    setShowCartModal(true);
  };

  const handleCartSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const cartData = {
      cart_number: parseInt(cartFormData.cart_number),
      qr_code: cartFormData.qr_code,
      seats: cartFormData.seats ? parseInt(cartFormData.seats) : undefined,
      color: cartFormData.color || undefined,
      description: cartFormData.description || undefined
    };

    try {
      const token = await getToken();
      const url = editingCart 
        ? `${API_URL}/api/admin/carts/${editingCart.id}`
        : `${API_URL}/api/admin/carts`;
      
      const response = await fetch(url, {
        method: editingCart ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cartData)
      });

      if (response.ok) {
        alert(`Cart ${editingCart ? 'updated' : 'created'} successfully!`);
        setShowCartModal(false);
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Operation failed');
      }
    } catch (error) {
      alert('Operation failed');
    }
  };

  const handleDeleteCart = async (cartId: string) => {
    if (!confirm('Are you sure you want to delete this cart?')) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/carts/${cartId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Cart deleted successfully!');
        fetchCarts();
      } else {
        alert('Failed to delete cart');
      }
    } catch (error) {
      alert('Failed to delete cart');
    }
  };

  // Toggle cart service status
  const handleToggleServiceStatus = async (cartId: string, currentStatus: boolean) => {
    if (!confirm(`Are you sure you want to mark this cart as ${currentStatus ? 'IN SERVICE' : 'OUT OF SERVICE'}?`)) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/carts/${cartId}/service-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ out_of_service: !currentStatus })
      });

      if (response.ok) {
        alert(`Cart marked as ${!currentStatus ? 'OUT OF SERVICE' : 'IN SERVICE'}`);
        await fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update service status');
      }
    } catch (error) {
      alert('Failed to update service status');
    }
  };

  // Upload QR image
  const handleQRImageUpload = async (cartId: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploadingQR(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('qr_image', file);

      const response = await fetch(`${API_URL}/api/admin/carts/${cartId}/qr-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('QR image uploaded successfully!');
        await fetchCarts();
        setShowQRModal(false);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to upload QR image');
      }
    } catch (error) {
      alert('Failed to upload QR image');
    } finally {
      setUploadingQR(false);
    }
  };

  // Upload cart images
  const handleCartImagesUpload = async (cartId: string, files: FileList) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Please select image files');
      return;
    }

    if (imageFiles.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }

    setUploadingImages(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      imageFiles.forEach(file => {
        formData.append('cart_images', file);
      });

      const response = await fetch(`${API_URL}/api/admin/carts/${cartId}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('Cart images uploaded successfully!');
        await fetchCarts();
        const updatedCarts = carts;
        const updatedCart = updatedCarts.find(c => c.id === cartId);
        if (updatedCart) {
          setSelectedCart(updatedCart);
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to upload cart images');
      }
    } catch (error) {
      alert('Failed to upload cart images');
    } finally {
      setUploadingImages(false);
    }
  };

  // Download QR image
  const handleDownloadQR = (url: string, cartNumber: number) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `cart-${cartNumber}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Delete cart image
  const handleDeleteCartImage = async (cartId: string, imageUrl: string) => {
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/carts/${cartId}/images`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_url: imageUrl })
      });

      if (response.ok) {
        alert('Image deleted successfully!');
        await fetchCarts();
        if (selectedCart) {
          const updatedImages = selectedCart.cart_images?.filter(img => img !== imageUrl) || [];
          setSelectedCart({ ...selectedCart, cart_images: updatedImages });
          if (currentImageIndex >= updatedImages.length) {
            setCurrentImageIndex(Math.max(0, updatedImages.length - 1));
          }
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete image');
      }
    } catch (error) {
      alert('Failed to delete image');
    }
  };

  // ========== USER MANAGEMENT (INVITATION-BASED) ==========

  const openUserModal = (user?: AdminUser) => {
    if (user) {
      setEditingUser(user);
      setUserFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        isAdmin: user.isAdmin
      });
    } else {
      setEditingUser(null);
      setUserFormData({ name: '', email: '', phone: '', isAdmin: false });
    }
    setShowUserModal(true);
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/invite-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userFormData.email,
          name: userFormData.name,
          phone: userFormData.phone,
          is_admin: userFormData.isAdmin
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert('Invitation sent successfully!');
        
        // Show magic link in console for testing
        if (data.magicLink) {
          console.log('Magic Link (for testing):', data.magicLink);
        }
        
        setShowUserModal(false);
        fetchUsers();
        fetchPendingInvitations();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to send invitation');
      }
    } catch (error) {
      alert('Failed to send invitation');
    }
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUser) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: userFormData.name,
          email: userFormData.email,
          phone: userFormData.phone,
          isAdmin: userFormData.isAdmin
        })
      });

      if (response.ok) {
        alert('User updated successfully!');
        setShowUserModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.error || 'Update failed');
      }
    } catch (error) {
      alert('Update failed');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('User deleted successfully!');
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete user');
      }
    } catch (error) {
      alert('Failed to delete user');
    }
  };

  const handlePasswordReset = async () => {
    if (!resetPassword || !resetUserId) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/users/${resetUserId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: resetPassword })
      });

      if (response.ok) {
        alert('Password reset successfully!');
        setShowPasswordReset(false);
        setResetPassword('');
        setResetUserId('');
      } else {
        alert('Password reset failed');
      }
    } catch (error) {
      alert('Password reset failed');
    }
  };

  // ========== INVITATION MANAGEMENT ==========

  const handleResendInvitation = async (invitationId: string) => {
    if (!confirm('Resend invitation email?')) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/resend-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invitationId })
      });

      if (response.ok) {
        const data = await response.json();
        alert('Invitation resent successfully!');
        
        // Show magic link in console for testing
        if (data.magicLink) {
          console.log('Magic Link (for testing):', data.magicLink);
        }
        
        fetchPendingInvitations();
      } else {
        alert('Failed to resend invitation');
      }
    } catch (error) {
      alert('Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Cancel this invitation? This will remove the pending user.')) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/cancel-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invitationId })
      });

      if (response.ok) {
        alert('Invitation cancelled successfully!');
        fetchPendingInvitations();
        fetchUsers();
      } else {
        alert('Failed to cancel invitation');
      }
    } catch (error) {
      alert('Failed to cancel invitation');
    }
  };

  // ========== BULK INVITE ==========

  const handleBulkInvite = async () => {
    if (!bulkInviteFile) { alert('Please select a CSV file'); return; }
    setBulkInviteLoading(true);
    setBulkInviteResults(null);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('csv', bulkInviteFile);
      const response = await fetch(`${API_URL}/api/admin/bulk-invite-users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      setBulkInviteResults(data.results || data);
      if (response.ok || data.results) { fetchUsers(); fetchPendingInvitations(); }
    } catch (error) {
      setBulkInviteResults({ error: 'Network error - please try again' });
    } finally {
      setBulkInviteLoading(false);
    }
  };

  const closeBulkInviteModal = () => {
    setShowBulkInviteModal(false);
    setBulkInviteFile(null);
    setBulkInviteResults(null);
    setBulkInviteLoading(false);
  };

  // ========== ADMIN CHECKOUT ACTIONS ==========

  const handleAdminCheckin = async (checkoutId: string, cartId: string, userId: string) => {
    if (!confirm('Check in this cart on behalf of the user?')) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/checkin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ checkoutId, cartId, userId })
      });

      if (response.ok) {
        alert('Cart checked in successfully!');
        fetchCheckouts();
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Check-in failed');
      }
    } catch (error) {
      alert('Check-in failed');
    }
  };

  const openAdminTransferModal = async (checkoutId: string, cartId: string, currentUserId: string) => {
    setAdminTransferCheckoutId(checkoutId);
    setAdminTransferCartId(cartId);
    setAdminTransferCurrentUserId(currentUserId);
    setShowAdminTransfer(true);
    setAdminTransferSearch('');
    setSelectedTransferUser(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const filteredUsers = data.filter((u: any) => u.id !== currentUserId);
        setAdminTransferUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const handleAdminTransfer = async () => {
    if (!selectedTransferUser) {
      alert('Please select a user to transfer to');
      return;
    }

    if (!confirm(`Transfer cart to ${selectedTransferUser.name}?`)) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          checkoutId: adminTransferCheckoutId,
          cartId: adminTransferCartId,
          newUserId: selectedTransferUser.id,
          fromUserId: adminTransferCurrentUserId
        })
      });

      if (response.ok) {
        alert('Cart transferred successfully!');
        setShowAdminTransfer(false);
        fetchCheckouts();
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Transfer failed');
      }
    } catch (error) {
      alert('Transfer failed');
    }
  };

  const openAdminCheckoutModal = async (cartId: string) => {
    setAdminCheckoutCartId(cartId);
    setShowAdminCheckout(true);
    setAdminCheckoutSearch('');
    setSelectedCheckoutUser(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminCheckoutUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const handleAdminCheckout = async () => {
    if (!selectedCheckoutUser) {
      alert('Please select a user to check out to');
      return;
    }

    if (!confirm(`Check out cart to ${selectedCheckoutUser.name}?`)) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/admin/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cartId: adminCheckoutCartId,
          userId: selectedCheckoutUser.id
        })
      });

      if (response.ok) {
        alert('Cart checked out successfully!');
        setShowAdminCheckout(false);
        fetchCarts();
        fetchCheckouts();
      } else {
        const error = await response.json();
        alert(error.error || 'Checkout failed');
      }
    } catch (error) {
      alert('Checkout failed');
    }
  };

  // ========== SORTING AND FILTERING ==========

  const handleUserSort = (field: 'name' | 'email' | 'createdAt') => {
    if (userSortField === field) {
      setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortField(field);
      setUserSortDirection('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal: string | number = a[userSortField];
    let bVal: string | number = b[userSortField];
    
    if (userSortField === 'createdAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    
    if (userSortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const sortedLogs = [...auditLogs].sort((a, b) => {
    const aTime = new Date(a.created_at).getTime();
    const bTime = new Date(b.created_at).getTime();
    return logSortDirection === 'desc' ? bTime - aTime : aTime - bTime;
  });

  const filteredCarts = carts.filter(cart => {
    if (!cartSearch) return true;
    const search = cartSearch.toLowerCase();
    return (
      cart.cart_number.toString().includes(search) ||
      cart.qr_code.toLowerCase().includes(search) ||
      cart.status.toLowerCase().includes(search) ||
      cart.color?.toLowerCase().includes(search) ||
      cart.description?.toLowerCase().includes(search)
    );
  });

  const filteredUsers = sortedUsers.filter(u => {
    if (!userSearch) return true;
    const search = userSearch.toLowerCase();
    return (
      u.name.toLowerCase().includes(search) ||
      u.email.toLowerCase().includes(search) ||
      u.phone?.toLowerCase().includes(search)
    );
  });

  const filteredTransferUsers = adminTransferUsers.filter(u => {
    if (!adminTransferSearch) return true;
    const search = adminTransferSearch.toLowerCase();
    return (
      u.name.toLowerCase().includes(search) ||
      u.email.toLowerCase().includes(search)
    );
  });

  const filteredCheckoutUsers = adminCheckoutUsers.filter(u => {
    if (!adminCheckoutSearch) return true;
    const search = adminCheckoutSearch.toLowerCase();
    return (
      u.name.toLowerCase().includes(search) ||
      u.email.toLowerCase().includes(search)
    );
  });

  const exportAuditLogs = () => {
    const ws = XLSX.utils.json_to_sheet(sortedLogs.map(log => ({
      Time: new Date(log.created_at).toLocaleString(),
      User: log.users?.name || 'System',
      Action: log.action,
      Cart: log.carts ? `Cart #${log.carts.cart_number}` : '-',
      Details: log.details ? JSON.stringify(log.details) : ''
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
    XLSX.writeFile(wb, `Audit_Logs_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const SortIcon = ({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) => (
    <Icon 
      icon={active ? (direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down') : 'mdi:unfold-more-horizontal'} 
      className={`inline ml-1 ${active ? 'text-purple-600' : 'text-gray-400'}`}
      width="16"
    />
  );

  // Helper function to render person silhouettes based on seat count
  const renderSeatIcons = (seats: number) => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: seats }).map((_, index) => (
          <Icon key={index} icon="mdi:account" width="14" className="text-gray-600" />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Icon icon="mdi:shield-crown" className="text-purple-600" width="36" />
              Admin Dashboard
            </h1>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium flex items-center gap-2"
            >
              <Icon icon="mdi:arrow-left" width="20" />
              Back to Main
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('checkouts')}
              className={`flex-shrink-0 px-6 py-4 font-medium transition whitespace-nowrap ${
                activeTab === 'checkouts'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Active Checkouts
            </button>
            <button
              onClick={() => setActiveTab('carts')}
              className={`flex-shrink-0 px-6 py-4 font-medium transition whitespace-nowrap ${
                activeTab === 'carts'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Manage Carts
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-shrink-0 px-6 py-4 font-medium transition whitespace-nowrap ${
                activeTab === 'users'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Manage Users
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex-shrink-0 px-6 py-4 font-medium transition whitespace-nowrap ${
                activeTab === 'logs'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Audit Logs
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-shrink-0 px-6 py-4 font-medium transition whitespace-nowrap ${
                activeTab === 'stats'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Statistics
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`flex-shrink-0 px-6 py-4 font-medium transition whitespace-nowrap ${
                activeTab === 'config'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Configuration
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="text-xl text-gray-600">Loading...</div>
              </div>
            ) : (
              <>
                {/* Active Checkouts Tab */}
                {activeTab === 'checkouts' && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Active Checkouts ({activeCheckouts.length})</h2>
                    {activeCheckouts.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Icon icon="mdi:cart-outline" className="mx-auto mb-4" width="64" />
                        <p>No active checkouts</p>
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {activeCheckouts.map(checkout => {
                          const checkoutDate = new Date(checkout.checked_out_at);
                          const dateStr = checkoutDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
                          const timeStr = checkoutDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

                          return (
                            <div key={checkout.id} className="border-2 border-purple-200 rounded-xl p-4 bg-purple-50">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-3xl font-bold text-purple-700">
                                  Cart #{checkout.carts.cart_number}
                                </div>
                              </div>
                              <div className="text-sm text-gray-700 mb-1">
                                <Icon icon="mdi:account" className="inline mr-1" width="16" />
                                <strong>{checkout.users.name}</strong>
                              </div>
                              <div className="text-sm text-gray-600 mb-1">
                                <Icon icon="mdi:email" className="inline mr-1" width="16" />
                                {checkout.users.email}
                              </div>
                              {checkout.users.phone && (
                                <div className="text-sm text-gray-600 mb-2">
                                  <Icon icon="mdi:phone" className="inline mr-1" width="16" />
                                  {checkout.users.phone}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mb-3">
                                Since {timeStr} on {dateStr}
                              </div>
                              <div className="space-y-2">
                                <button
                                  onClick={() => handleAdminCheckin(checkout.id, checkout.carts.id, checkout.user_id)}
                                  className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                                >
                                  Check In
                                </button>
                                <button
                                  onClick={() => openAdminTransferModal(checkout.id, checkout.carts.id, checkout.user_id)}
                                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                                >
                                  Transfer
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Manage Carts Tab */}
                {activeTab === 'carts' && (
                  <div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-4">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Carts ({filteredCarts.length}{cartSearch && ` of ${carts.length}`})
                      </h2>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:flex-1 sm:max-w-md">
                        <div className="flex gap-2 flex-1">
                          <input
                            type="text"
                            value={cartSearch}
                            onChange={(e) => setCartSearch(e.target.value)}
                            placeholder="Search carts..."
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          {cartSearch && (
                            <button
                              onClick={() => setCartSearch('')}
                              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              <Icon icon="mdi:close" width="20" />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => openCartModal()}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <Icon icon="mdi:plus" width="20" />
                          Add Cart
                        </button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Cart #</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">QR Code</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Images</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Service</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredCarts.map((cart) => (
                            <tr key={cart.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                #{cart.cart_number}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {cart.seats && (
                                  <div className="flex items-center gap-1">
                                    {renderSeatIcons(cart.seats)}
                                    <span>{cart.seats} {cart.seats === 1 ? 'seat' : 'seats'}</span>
                                  </div>
                                )}
                                {cart.color && (
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-4 h-4 rounded-full border border-gray-300" 
                                      style={{ backgroundColor: cart.color.toLowerCase() }}
                                    />
                                    <span className="text-xs">{cart.color}</span>
                                  </div>
                                )}
                                {cart.description && <div className="text-xs text-gray-500 italic mt-1">{cart.description}</div>}
                                <div className="text-xs text-gray-400 mt-1">QR: {cart.qr_code}</div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  cart.status === 'available' ? 'bg-green-100 text-green-800' :
                                  cart.status === 'checked_out' ? 'bg-orange-100 text-orange-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {cart.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex gap-2 flex-wrap">
                                  {cart.qr_image_url ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          setSelectedCart(cart);
                                          setShowQRModal(true);
                                        }}
                                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs flex items-center gap-1"
                                      >
                                        <Icon icon="mdi:qrcode" width="14" />
                                        View
                                      </button>
                                      <button
                                        onClick={() => handleDownloadQR(cart.qr_image_url!, cart.cart_number)}
                                        className="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs flex items-center gap-1"
                                      >
                                        <Icon icon="mdi:download" width="14" />
                                      </button>
                                    </>
                                  ) : (
                                    <label className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs cursor-pointer flex items-center gap-1">
                                      <Icon icon="mdi:upload" width="14" />
                                      Upload
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                          if (e.target.files?.[0]) {
                                            handleQRImageUpload(cart.id, e.target.files[0]);
                                          }
                                        }}
                                      />
                                    </label>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex gap-2 flex-wrap">
                                  {cart.cart_images && cart.cart_images.length > 0 ? (
                                    <button
                                      onClick={() => {
                                        setSelectedCart(cart);
                                        setCurrentImageIndex(0);
                                        setShowImagesModal(true);
                                      }}
                                      className="px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-xs flex items-center gap-1"
                                    >
                                      <Icon icon="mdi:image-multiple" width="14" />
                                      View ({cart.cart_images.length})
                                    </button>
                                  ) : (
                                    <label className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs cursor-pointer flex items-center gap-1">
                                      <Icon icon="mdi:upload" width="14" />
                                      Upload
                                      <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                          if (e.target.files) {
                                            handleCartImagesUpload(cart.id, e.target.files);
                                          }
                                        }}
                                      />
                                    </label>
                                  )}
                                  {cart.cart_images && cart.cart_images.length > 0 && cart.cart_images.length < 5 && (
                                    <label className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs cursor-pointer flex items-center gap-1">
                                      <Icon icon="mdi:plus" width="14" />
                                      Add
                                      <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                          if (e.target.files) {
                                            handleCartImagesUpload(cart.id, e.target.files);
                                          }
                                        }}
                                      />
                                    </label>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <button
                                  onClick={() => handleToggleServiceStatus(cart.id, cart.out_of_service || false)}
                                  className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                                    cart.out_of_service
                                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  <Icon icon={cart.out_of_service ? "mdi:tools" : "mdi:check-circle"} width="14" />
                                  {cart.out_of_service ? 'Out' : 'Active'}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-sm space-x-2">
                                {cart.status === 'available' && !cart.out_of_service && (
                                  <button
                                    onClick={() => openAdminCheckoutModal(cart.id)}
                                    className="text-green-600 hover:text-green-800 font-medium"
                                  >
                                    Check Out
                                  </button>
                                )}
                                <button
                                  onClick={() => openCartModal(cart)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteCart(cart.id)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Manage Users Tab */}
                {activeTab === 'users' && (
                  <div>
                    {/* Pending Invitations Section */}
                    {pendingInvitations.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                          Pending Invitations ({pendingInvitations.length})
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg mb-6">
                            <thead className="bg-yellow-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Invited By</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Expires</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {pendingInvitations.map(invite => (
                                <tr key={invite.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">{invite.email}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{invite.name}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">
                                    {invite.inviter?.name || 'Unknown'}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600">
                                    {new Date(invite.invited_at).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600">
                                    {new Date(invite.expires_at).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm space-x-2">
                                    <button
                                      onClick={() => handleResendInvitation(invite.id)}
                                      className="text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                      Resend
                                    </button>
                                    <button
                                      onClick={() => handleCancelInvitation(invite.id)}
                                      className="text-red-600 hover:text-red-800 font-medium"
                                    >
                                      Cancel
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Active Users Section */}
                    <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Users ({filteredUsers.length}{userSearch && ` of ${users.length}`})
                      </h2>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Search users..."
                            className="w-44 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          {userSearch && (
                            <button
                              onClick={() => setUserSearch('')}
                              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              <Icon icon="mdi:close" width="20" />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => openUserModal()}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 whitespace-nowrap"
                        >
                          <Icon icon="mdi:account-plus" width="20" />
                          Invite User
                        </button>
                        <button
                          onClick={() => { setBulkInviteResults(null); setBulkInviteFile(null); setShowBulkInviteModal(true); }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2 whitespace-nowrap"
                        >
                          <Icon icon="mdi:account-multiple-plus" width="20" />
                          Bulk Invite CSV
                        </button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead className="bg-gray-50">
                          <tr>
                            <th 
                              className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                              onClick={() => handleUserSort('name')}
                            >
                              Name
                              <SortIcon active={userSortField === 'name'} direction={userSortDirection} />
                            </th>
                            <th 
                              className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                              onClick={() => handleUserSort('email')}
                            >
                              Email
                              <SortIcon active={userSortField === 'email'} direction={userSortDirection} />
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Phone</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                            <th 
                              className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                              onClick={() => handleUserSort('createdAt')}
                            >
                              Created
                              <SortIcon active={userSortField === 'createdAt'} direction={userSortDirection} />
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredUsers.map((u) => (
                            <tr key={u.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{u.phone || '-'}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  u.isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {u.isAdmin ? 'Admin' : 'User'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  u.status === 'active' ? 'bg-green-100 text-green-800' :
                                  u.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {u.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(u.createdAt).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm space-x-2">
                                <button
                                  onClick={() => openUserModal(u)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setResetUserId(u.id);
                                    setShowPasswordReset(true);
                                  }}
                                  className="text-orange-600 hover:text-orange-800 font-medium"
                                >
                                  Reset Password
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Audit Logs Tab */}
                {activeTab === 'logs' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">Audit Logs ({auditLogs.length})</h2>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setLogSortDirection(logSortDirection === 'desc' ? 'asc' : 'desc')}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium flex items-center gap-2"
                        >
                          <Icon icon={logSortDirection === 'desc' ? 'mdi:sort-clock-descending' : 'mdi:sort-clock-ascending'} width="20" />
                          {logSortDirection === 'desc' ? 'Newest First' : 'Oldest First'}
                        </button>
                        <button
                          onClick={exportAuditLogs}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2"
                        >
                          <Icon icon="mdi:file-excel" width="20" />
                          Export to Excel
                        </button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">User</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Action</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Cart</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(log.created_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {log.users?.name || 'System'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  log.action.includes('checkout') ? 'bg-blue-100 text-blue-800' :
                                  log.action.includes('checkin') ? 'bg-green-100 text-green-800' :
                                  log.action.includes('transfer') ? 'bg-purple-100 text-purple-800' :
                                  log.action.includes('delete') ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {log.action}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {log.carts ? `Cart #${log.carts.cart_number}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {log.action === 'admin_checkin' && log.details?.checked_in_user_name && (
                                  <div className="text-xs">
                                    <div className="font-medium">Checked in on behalf of:</div>
                                    <div>{log.details.checked_in_user_name}</div>
                                    <div className="text-gray-400">{log.details.checked_in_user_email}</div>
                                  </div>
                                )}
                                {log.action === 'admin_checkout' && log.details?.checked_out_user_name && (
                                  <div className="text-xs">
                                    <div className="font-medium">Checked out on behalf of:</div>
                                    <div>{log.details.checked_out_user_name}</div>
                                    <div className="text-gray-400">{log.details.checked_out_user_email}</div>
                                  </div>
                                )}
                                {log.action === 'admin_transfer' && log.details?.from_user_name && (
                                  <div className="text-xs">
                                    <div className="font-medium">From: {log.details.from_user_name}</div>
                                    <div className="text-gray-400">{log.details.from_user_email}</div>
                                    <div className="font-medium mt-1">To: {log.details.to_user_name}</div>
                                    <div className="text-gray-400">{log.details.to_user_email}</div>
                                  </div>
                                )}
                                {log.action === 'admin_cart_updated' && log.details?.changes && (
                                  <div className="text-xs">
                                    {Object.keys(log.details.changes).length > 0 ? (
                                      <div>
                                        <div className="font-medium mb-1">Changed fields:</div>
                                        {Object.entries(log.details.changes).map(([field, values]: [string, any]) => (
                                          <div key={field} className="mb-1">
                                            <span className="font-medium">{field}:</span>
                                            <div className="text-gray-400 ml-2">
                                              {values.old !== null && values.old !== undefined ? values.old : '(empty)'} → {values.new !== null && values.new !== undefined ? values.new : '(empty)'}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No changes</span>
                                    )}
                                  </div>
                                )}
                                {log.action === 'admin_user_updated' && log.details?.changes && (
                                  <div className="text-xs">
                                    <div className="font-medium">User: {log.details.target_name}</div>
                                    <div className="text-gray-400 mb-1">{log.details.target_email}</div>
                                    {Object.keys(log.details.changes).length > 0 ? (
                                      <div>
                                        <div className="font-medium mb-1">Changed fields:</div>
                                        {Object.entries(log.details.changes).map(([field, values]: [string, any]) => (
                                          <div key={field} className="mb-1">
                                            <span className="font-medium">{field}:</span>
                                            <div className="text-gray-400 ml-2">
                                              {values.old !== null && values.old !== undefined ? String(values.old) : '(empty)'} → {values.new !== null && values.new !== undefined ? String(values.new) : '(empty)'}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No changes</span>
                                    )}
                                  </div>
                                )}
                                {log.action === 'admin_user_password_reset' && log.details?.target_name && (
                                  <div className="text-xs">
                                    <div className="font-medium">User: {log.details.target_name}</div>
                                    <div className="text-gray-400">{log.details.target_email}</div>
                                    <div className="text-orange-600 mt-1">{log.details.note}</div>
                                  </div>
                                )}
                                {log.action === 'user_invited' && log.details?.invited_email && (
                                  <div className="text-xs">
                                    <div className="font-medium">Invited: {log.details.invited_name}</div>
                                    <div className="text-gray-400">{log.details.invited_email}</div>
                                  </div>
                                )}
                                {log.action === 'invitation_cancelled' && log.details?.cancelled_email && (
                                  <div className="text-xs">
                                    <div className="font-medium">Cancelled invitation for: {log.details.cancelled_name}</div>
                                    <div className="text-gray-400">{log.details.cancelled_email}</div>
                                    {log.details.user_deleted && (
                                      <div className="text-red-600 mt-1">Pending user account deleted</div>
                                    )}
                                    {log.details.was_accepted && (
                                      <div className="text-blue-600 mt-1">User had already accepted - account kept</div>
                                    )}
                                  </div>
                                )}
                                {log.action === 'admin_user_deleted' && log.details?.deleted_user_name && (
                                  <div className="text-xs">
                                    <div className="font-medium">Deleted user: {log.details.deleted_user_name}</div>
                                    <div className="text-gray-400">{log.details.deleted_user_email}</div>
                                  </div>
                                )}
                                {log.action === 'invitation_accepted' && log.details?.email && (
                                  <div className="text-xs">
                                    <div className="font-medium">Accepted by: {log.details.email}</div>
                                  </div>
                                )}
                                {log.action === 'transfer' && log.details?.to_email && (
                                  <div className="text-xs">→ {log.details.to_email}</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Statistics Tab */}
                {activeTab === 'stats' && statistics && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Cart Usage Statistics</h2>

                    {/* Daily Average Metrics Row */}
                    <div className="grid gap-6 md:grid-cols-2 mb-6">
                      {/* Avg Cart Uses Per Day */}
                      <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-300 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <Icon icon="mdi:cart-arrow-right" className="text-teal-600" width="32" />
                          <h3 className="text-lg font-bold text-gray-800">Avg Cart Uses / Day</h3>
                        </div>
                        <div className="text-5xl font-bold text-teal-700 mb-2">
                          {statistics.avgUsesPerDay ?? '—'}
                        </div>
                        <div className="text-sm text-gray-600">
                          completed checkouts per day
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          averaged over last 30 days
                        </div>
                      </div>

                      {/* Avg Active Users Per Day */}
                      <div className="bg-gradient-to-br from-sky-50 to-sky-100 border-2 border-sky-300 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <Icon icon="mdi:account-group" className="text-sky-600" width="32" />
                          <h3 className="text-lg font-bold text-gray-800">Avg Active Users / Day</h3>
                        </div>
                        <div className="text-5xl font-bold text-sky-700 mb-2">
                          {statistics.avgActiveUsersPerDay ?? '—'}
                        </div>
                        <div className="text-sm text-gray-600">
                          unique users checking out or in
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          averaged over last 30 days
                        </div>
                      </div>
                    </div>

                    {/* Daily Usage Chart */}
                    {statistics.dailyUsage && statistics.dailyUsage.length > 0 && (() => {
                      const data = statistics.dailyUsage as { date: string; uses: number; activeUsers: number }[];
                      const maxUses = Math.max(...data.map(d => d.uses), 1);
                      const maxUsers = Math.max(...data.map(d => d.activeUsers), 1);
                      const overallMax = Math.max(maxUses, maxUsers, 1);

                      // Chart dimensions
                      const chartW = 800;
                      const chartH = 200;
                      const padLeft = 36;
                      const padRight = 12;
                      const padTop = 12;
                      const padBottom = 44;
                      const plotW = chartW - padLeft - padRight;
                      const plotH = chartH - padTop - padBottom;
                      const barGroupW = plotW / data.length;
                      const barW = Math.max(Math.floor(barGroupW * 0.36), 3);
                      const gap = Math.max(Math.floor(barGroupW * 0.08), 1);

                      // Y-axis grid lines
                      const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => ({
                        y: padTop + plotH - t * plotH,
                        label: Math.round(t * overallMax)
                      }));

                      // Label every ~7 days to avoid crowding
                      const labelStep = data.length <= 14 ? 2 : data.length <= 21 ? 3 : 7;

                      return (
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon icon="mdi:chart-bar" className="text-purple-600" width="28" />
                            <h3 className="text-xl font-bold text-gray-800">Daily Cart Usage — Last 30 Days</h3>
                          </div>
                          <div className="flex items-center gap-6 mb-4 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded bg-teal-500 opacity-90" />
                              <span className="text-gray-600">Cart Uses</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded bg-sky-400 opacity-80" />
                              <span className="text-gray-600">Active Users</span>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <svg
                              viewBox={`0 0 ${chartW} ${chartH}`}
                              className="w-full"
                              style={{ minWidth: '480px', maxHeight: '220px' }}
                              aria-label="Daily cart usage chart"
                            >
                              {/* Y-axis grid lines and labels */}
                              {yTicks.map((tick, i) => (
                                <g key={i}>
                                  <line
                                    x1={padLeft} y1={tick.y}
                                    x2={chartW - padRight} y2={tick.y}
                                    stroke="#e5e7eb" strokeWidth="1"
                                    strokeDasharray={tick.label === 0 ? undefined : '4 3'}
                                  />
                                  <text
                                    x={padLeft - 4} y={tick.y + 4}
                                    textAnchor="end"
                                    fontSize="10"
                                    fill="#9ca3af"
                                  >
                                    {tick.label}
                                  </text>
                                </g>
                              ))}

                              {/* Bars */}
                              {data.map((d, i) => {
                                const groupX = padLeft + i * barGroupW;
                                const usesH = overallMax > 0 ? (d.uses / overallMax) * plotH : 0;
                                const usersH = overallMax > 0 ? (d.activeUsers / overallMax) * plotH : 0;
                                const usesY = padTop + plotH - usesH;
                                const usersY = padTop + plotH - usersH;
                                const centerX = groupX + barGroupW / 2;
                                const usesBarX = centerX - barW - gap / 2;
                                const usersBarX = centerX + gap / 2;

                                // X-axis date label
                                const showLabel = i % labelStep === 0 || i === data.length - 1;
                                const labelDate = new Date(d.date + 'T00:00:00');
                                const labelStr = `${labelDate.getMonth() + 1}/${labelDate.getDate()}`;

                                return (
                                  <g key={d.date}>
                                    {/* Uses bar */}
                                    {d.uses > 0 && (
                                      <rect
                                        x={usesBarX} y={usesY}
                                        width={barW} height={usesH}
                                        fill="#14b8a6" opacity="0.9" rx="2"
                                      >
                                        <title>{`${d.date}: ${d.uses} use${d.uses !== 1 ? 's' : ''}`}</title>
                                      </rect>
                                    )}
                                    {/* Active users bar */}
                                    {d.activeUsers > 0 && (
                                      <rect
                                        x={usersBarX} y={usersY}
                                        width={barW} height={usersH}
                                        fill="#38bdf8" opacity="0.8" rx="2"
                                      >
                                        <title>{`${d.date}: ${d.activeUsers} active user${d.activeUsers !== 1 ? 's' : ''}`}</title>
                                      </rect>
                                    )}
                                    {/* X-axis label */}
                                    {showLabel && (
                                      <text
                                        x={centerX} y={chartH - padBottom + 14}
                                        textAnchor="middle"
                                        fontSize="9"
                                        fill="#6b7280"
                                      >
                                        {labelStr}
                                      </text>
                                    )}
                                  </g>
                                );
                              })}

                              {/* X-axis baseline */}
                              <line
                                x1={padLeft} y1={padTop + plotH}
                                x2={chartW - padRight} y2={padTop + plotH}
                                stroke="#d1d5db" strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                          <div className="text-xs text-gray-400 text-right mt-1">
                            Hover bars for exact counts
                          </div>
                        </div>
                      );
                    })()}

                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-6">
                      {/* Most Frequently Used Cart */}
                      {statistics.mostFrequentCart && (
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Icon icon="mdi:trophy" className="text-green-600" width="32" />
                            <h3 className="text-lg font-bold text-gray-800">Most Used Cart</h3>
                          </div>
                          <div className="text-4xl font-bold text-green-700 mb-2">
                            #{statistics.mostFrequentCart.cart_number}
                          </div>
                          <div className="text-sm text-gray-600">
                            {statistics.mostFrequentCart.usage_count} checkouts
                          </div>
                          {statistics.mostFrequentCart.color && (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: statistics.mostFrequentCart.color.toLowerCase() }} />
                              <span className="text-xs text-gray-500">{statistics.mostFrequentCart.color}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Least Frequently Used Cart */}
                      {statistics.leastFrequentCart && (
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Icon icon="mdi:alert-circle" className="text-orange-600" width="32" />
                            <h3 className="text-lg font-bold text-gray-800">Least Used Cart</h3>
                          </div>
                          <div className="text-4xl font-bold text-orange-700 mb-2">
                            #{statistics.leastFrequentCart.cart_number}
                          </div>
                          <div className="text-sm text-gray-600">
                            {statistics.leastFrequentCart.usage_count} checkouts
                          </div>
                          {statistics.leastFrequentCart.color && (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: statistics.leastFrequentCart.color.toLowerCase() }} />
                              <span className="text-xs text-gray-500">{statistics.leastFrequentCart.color}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Longest Cart Use */}
                      {statistics.longestUse && (
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Icon icon="mdi:clock-outline" className="text-purple-600" width="32" />
                            <h3 className="text-lg font-bold text-gray-800">Longest Use</h3>
                          </div>
                          <div className="text-4xl font-bold text-purple-700 mb-2">
                            #{statistics.longestUse.cart_number}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            {statistics.longestUse.duration}
                          </div>
                          <div className="text-xs text-gray-500">
                            by {statistics.longestUse.user_name}
                          </div>
                        </div>
                      )}

                      {/* Shortest Cart Use */}
                      {statistics.shortestUse && (
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Icon icon="mdi:timer-outline" className="text-blue-600" width="32" />
                            <h3 className="text-lg font-bold text-gray-800">Shortest Use</h3>
                          </div>
                          <div className="text-4xl font-bold text-blue-700 mb-2">
                            #{statistics.shortestUse.cart_number}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            {statistics.shortestUse.duration}
                          </div>
                          <div className="text-xs text-gray-500">
                            by {statistics.shortestUse.user_name}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Top 5 Users */}
                    {statistics.topUsers && statistics.topUsers.length > 0 && (
                      <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Icon icon="mdi:account-star" className="text-indigo-600" width="32" />
                          <h3 className="text-xl font-bold text-gray-800">Top 5 Users</h3>
                        </div>
                        <div className="space-y-3">
                          {statistics.topUsers.map((user: any, index: number) => (
                            <div key={user.user_id} className="flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-white ${
                                  index === 0 ? 'bg-yellow-500' :
                                  index === 1 ? 'bg-gray-400' :
                                  index === 2 ? 'bg-orange-600' :
                                  'bg-indigo-500'
                                }`}>
                                  {index + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-gray-800 truncate">{user.user_name}</div>
                                  <div className="text-xs text-gray-500 truncate">{user.user_email}</div>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-2xl font-bold text-indigo-600">{user.checkout_count}</div>
                                <div className="text-xs text-gray-500 whitespace-nowrap">checkouts</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Configuration Tab */}
                {activeTab === 'config' && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Application Configuration</h2>
                    
                    <div className="max-w-2xl">
                      {/* Logo Upload */}
                      <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Icon icon="mdi:image" className="text-purple-600" width="32" />
                          <h3 className="text-xl font-bold text-gray-800">Login Page Logo</h3>
                        </div>
                        
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-4">
                            Upload a logo to display above the login box. Supports JPG, PNG, and SVG files.
                            Logo will be displayed in a 150px × 150px square.
                          </p>
                          
                          {/* Logo Preview */}
                          {logoPreview && (
                            <div className="mb-4 flex justify-center">
                              <div className="w-[150px] h-[150px] border-2 border-gray-300 rounded-xl overflow-hidden bg-white p-4">
                                <img 
                                  src={logoPreview} 
                                  alt="Logo Preview" 
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            </div>
                          )}
                          
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/svg+xml"
                            onChange={handleLogoUpload}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Tagline */}
                      <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Icon icon="mdi:text" className="text-purple-600" width="32" />
                          <h3 className="text-xl font-bold text-gray-800">Login Page Tagline</h3>
                        </div>
                        
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-4">
                            Add a tagline or slogan to display below the logo on the login page.
                          </p>
                          
                          <input
                            type="text"
                            value={configTagline}
                            onChange={(e) => setConfigTagline(e.target.value)}
                            placeholder="Enter tagline (e.g., 'Making golf cart management easy')"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          
                          {configTagline && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-500 mb-1">Preview:</p>
                              <p className="text-xl font-semibold text-gray-700 italic">
                                {configTagline}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={handleConfigSave}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-bold flex items-center gap-2"
                        >
                          <Icon icon="mdi:content-save" width="20" />
                          Save Configuration
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Cart Modal */}
        {showCartModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {editingCart ? 'Edit Cart' : 'Add New Cart'}
              </h3>
              <form onSubmit={handleCartSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cart Number *
                  </label>
                  <input
                    type="number"
                    value={cartFormData.cart_number}
                    onChange={(e) => setCartFormData({ ...cartFormData, cart_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    QR Code *
                  </label>
                  <input
                    type="text"
                    value={cartFormData.qr_code}
                    onChange={(e) => setCartFormData({ ...cartFormData, qr_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seats
                  </label>
                  <input
                    type="number"
                    value={cartFormData.seats}
                    onChange={(e) => setCartFormData({ ...cartFormData, seats: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <input
                    type="text"
                    value={cartFormData.color}
                    onChange={(e) => setCartFormData({ ...cartFormData, color: e.target.value })}
                    placeholder="e.g., Red, Blue, Green"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={cartFormData.description}
                    onChange={(e) => setCartFormData({ ...cartFormData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCartModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
                  >
                    {editingCart ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* User Modal (Invitation or Edit) */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {editingUser ? 'Edit User' : 'Invite New User'}
              </h3>
              <form onSubmit={editingUser ? handleUserSubmit : handleInviteUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={userFormData.name}
                    onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                {editingUser && (
                  <div className="text-sm text-gray-600 italic bg-blue-50 p-3 rounded-lg">
                    To change password, use the "Reset Password" button
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={userFormData.isAdmin}
                    onChange={(e) => setUserFormData({ ...userFormData, isAdmin: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="isAdmin" className="text-sm font-medium text-gray-700">
                    Administrator
                  </label>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowUserModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
                  >
                    {editingUser ? 'Update' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Reset User Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter new password"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordReset(false);
                      setResetPassword('');
                      setResetUserId('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasswordReset}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium"
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Transfer Modal */}
        {showAdminTransfer && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800">Transfer Cart (Admin)</h3>
                <p className="text-sm text-gray-500">Select the user to transfer this cart to.</p>
              </div>

              {/* Search Input */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Search name or email..."
                  value={adminTransferSearch}
                  onChange={(e) => setAdminTransferSearch(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all"
                  autoFocus
                />
              </div>

              {/* User List Container */}
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y">
                {filteredTransferUsers.length === 0 ? (
                  <div className="p-10 text-center text-gray-400">
                    {adminTransferSearch ? 'No matches found' : 'No users available'}
                  </div>
                ) : (
                  filteredTransferUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedTransferUser(u)}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                        selectedTransferUser?.id === u.id 
                          ? 'bg-blue-50 border-l-4 border-blue-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div>
                        <div className="font-semibold text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      {u.isAdmin && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold uppercase">
                          Admin
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Selected User Badge */}
              {selectedTransferUser && (
                <div className="mt-4 p-3 bg-blue-50 rounded-xl flex items-center gap-2">
                  <div className="bg-blue-500 text-white rounded-full p-1">
                    <Icon icon="mdi:check" width="16" />
                  </div>
                  <span className="text-sm font-medium text-blue-700">
                    Transferring to <strong>{selectedTransferUser.name}</strong>
                  </span>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdminTransfer(false);
                    setSelectedTransferUser(null);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!selectedTransferUser}
                  onClick={handleAdminTransfer}
                  className={`flex-1 px-4 py-3 rounded-xl font-bold transition ${
                    selectedTransferUser 
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Transfer Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Checkout Modal */}
        {showAdminCheckout && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800">Check Out Cart (Admin)</h3>
                <p className="text-sm text-gray-500">Select the user to check out this cart to.</p>
              </div>

              {/* Search Input */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Search name or email..."
                  value={adminCheckoutSearch}
                  onChange={(e) => setAdminCheckoutSearch(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:bg-white outline-none transition-all"
                  autoFocus
                />
              </div>

              {/* User List Container */}
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y">
                {filteredCheckoutUsers.length === 0 ? (
                  <div className="p-10 text-center text-gray-400">
                    {adminCheckoutSearch ? 'No matches found' : 'No users available'}
                  </div>
                ) : (
                  filteredCheckoutUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedCheckoutUser(u)}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                        selectedCheckoutUser?.id === u.id 
                          ? 'bg-green-50 border-l-4 border-green-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div>
                        <div className="font-semibold text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      {u.isAdmin && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold uppercase">
                          Admin
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Selected User Badge */}
              {selectedCheckoutUser && (
                <div className="mt-4 p-3 bg-green-50 rounded-xl flex items-center gap-2">
                  <div className="bg-green-500 text-white rounded-full p-1">
                    <Icon icon="mdi:check" width="16" />
                  </div>
                  <span className="text-sm font-medium text-green-700">
                    Checking out to <strong>{selectedCheckoutUser.name}</strong>
                  </span>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdminCheckout(false);
                    setSelectedCheckoutUser(null);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!selectedCheckoutUser}
                  onClick={handleAdminCheckout}
                  className={`flex-1 px-4 py-3 rounded-xl font-bold transition ${
                    selectedCheckoutUser 
                      ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-200' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Check Out Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Invite CSV Modal */}
        {showBulkInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Icon icon="mdi:account-multiple-plus" className="text-blue-600" width="28" />
                  Bulk Invite Users via CSV
                </h3>
                <button onClick={closeBulkInviteModal} className="text-gray-500 hover:text-gray-700">
                  <Icon icon="mdi:close" width="24" />
                </button>
              </div>
              {!bulkInviteResults && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
                  <div className="font-semibold mb-2 flex items-center gap-1">
                    <Icon icon="mdi:information" width="16" /> CSV Format Requirements
                  </div>
                  <p className="mb-2">Your CSV file must have these columns (header row required):</p>
                  <code className="block bg-white border border-blue-200 rounded p-2 text-xs font-mono mb-2">email,name,phone,is_admin</code>
                  <ul className="list-disc ml-4 space-y-1 text-xs">
                    <li><strong>email</strong> — required, must be unique</li>
                    <li><strong>name</strong> — required</li>
                    <li><strong>phone</strong> — optional</li>
                    <li><strong>is_admin</strong> — optional, true or false (defaults to false)</li>
                  </ul>
                  <p className="mt-2 text-xs">Duplicate emails are automatically skipped.</p>
                </div>
              )}
              {!bulkInviteResults && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition">
                    <Icon icon="mdi:file-delimited" className="mx-auto mb-2 text-gray-400" width="40" />
                    {bulkInviteFile ? (
                      <div>
                        <p className="text-sm font-medium text-gray-800">{bulkInviteFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{(bulkInviteFile.size / 1024).toFixed(1)} KB</p>
                        <button onClick={() => setBulkInviteFile(null)} className="mt-2 text-xs text-red-600 hover:text-red-800">Remove file</button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Drop your CSV here or click to browse</p>
                        <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm font-medium inline-block">
                          Browse CSV
                          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { if (e.target.files?.[0]) setBulkInviteFile(e.target.files[0]); }} />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {bulkInviteResults && (
                <div className="mb-6">
                  {bulkInviteResults.error ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      <div className="font-semibold flex items-center gap-1 mb-1"><Icon icon="mdi:alert-circle" width="16" /> Error</div>
                      {bulkInviteResults.error}
                    </div>
                  ) : (
                    <div>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-center">
                          <div className="text-2xl font-bold text-green-700">{bulkInviteResults.succeeded ?? 0}</div>
                          <div className="text-xs text-green-600 font-medium">Invited</div>
                        </div>
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
                          <div className="text-2xl font-bold text-yellow-700">{bulkInviteResults.skipped ?? 0}</div>
                          <div className="text-xs text-yellow-600 font-medium">Skipped</div>
                        </div>
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-center">
                          <div className="text-2xl font-bold text-red-700">{bulkInviteResults.failed ?? 0}</div>
                          <div className="text-xs text-red-600 font-medium">Failed</div>
                        </div>
                      </div>
                      {bulkInviteResults.details && bulkInviteResults.details.length > 0 && (
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">Row Details</div>
                          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                            {bulkInviteResults.details.map((d: any, i: number) => (
                              <div key={i} className="flex items-start gap-3 px-3 py-2 text-sm">
                                <div className="mt-0.5 flex-shrink-0">
                                  {d.status === 'success' && <Icon icon="mdi:check-circle" className="text-green-500" width="16" />}
                                  {d.status === 'skipped' && <Icon icon="mdi:skip-next-circle" className="text-yellow-500" width="16" />}
                                  {d.status === 'failed' && <Icon icon="mdi:close-circle" className="text-red-500" width="16" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-800 truncate">{d.email}</div>
                                  {d.status === 'skipped' && <div className="text-xs text-yellow-600">{d.reason}</div>}
                                  {d.status === 'failed' && <div className="text-xs text-red-600">{d.reason}</div>}
                                </div>
                                <div className="text-xs text-gray-400 flex-shrink-0">Row {d.row}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={closeBulkInviteModal} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition">
                  {bulkInviteResults ? 'Close' : 'Cancel'}
                </button>
                {!bulkInviteResults && (
                  <button type="button" disabled={!bulkInviteFile || bulkInviteLoading} onClick={handleBulkInvite}
                    className={`flex-1 px-4 py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 ${bulkInviteFile && !bulkInviteLoading ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                    {bulkInviteLoading ? (<><Icon icon="mdi:loading" className="animate-spin" width="20" />Processing...</>) : (<><Icon icon="mdi:send" width="20" />Send Invitations</>)}
                  </button>
                )}
                {bulkInviteResults && !bulkInviteResults.error && (
                  <button type="button" onClick={() => { setBulkInviteResults(null); setBulkInviteFile(null); }}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2">
                    <Icon icon="mdi:upload" width="20" />Upload Another
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* QR Image Modal */}
        {showQRModal && selectedCart && selectedCart.qr_image_url && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" 
            onClick={() => setShowQRModal(false)}
          >
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">QR Code - Cart #{selectedCart.cart_number}</h3>
                <button onClick={() => setShowQRModal(false)} className="text-gray-500 hover:text-gray-700">
                  <Icon icon="mdi:close" width="24" />
                </button>
              </div>
              <div className="flex justify-center mb-4">
                <img 
                  src={selectedCart.qr_image_url} 
                  alt={`QR Code for Cart ${selectedCart.cart_number}`}
                  className="max-w-full h-auto max-h-96 border-2 border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDownloadQR(selectedCart.qr_image_url!, selectedCart.cart_number)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
                >
                  <Icon icon="mdi:download" width="20" />
                  Download QR Code
                </button>
                <label className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-center cursor-pointer flex items-center justify-center gap-2">
                  <Icon icon="mdi:upload" width="20" />
                  Replace QR Code
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleQRImageUpload(selectedCart.id, e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Cart Images Modal with Carousel */}
        {showImagesModal && selectedCart && selectedCart.cart_images && selectedCart.cart_images.length > 0 && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" 
            onClick={() => setShowImagesModal(false)}
          >
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Cart Images - Cart #{selectedCart.cart_number}</h3>
                <button onClick={() => setShowImagesModal(false)} className="text-gray-500 hover:text-gray-700">
                  <Icon icon="mdi:close" width="24" />
                </button>
              </div>
              
              {/* Carousel */}
              <div className="relative mb-4">
                <div className="flex justify-center items-center bg-gray-100 rounded-lg" style={{ minHeight: '400px' }}>
                  <img 
                    src={selectedCart.cart_images[currentImageIndex]} 
                    alt={`Cart ${selectedCart.cart_number} - Image ${currentImageIndex + 1}`}
                    className="max-w-full max-h-[500px] h-auto object-contain rounded-lg"
                  />
                </div>
                
                {/* Navigation Arrows */}
                {selectedCart.cart_images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((currentImageIndex - 1 + selectedCart.cart_images!.length) % selectedCart.cart_images!.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 shadow-lg"
                    >
                      <Icon icon="mdi:chevron-left" width="24" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((currentImageIndex + 1) % selectedCart.cart_images!.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 shadow-lg"
                    >
                      <Icon icon="mdi:chevron-right" width="24" />
                    </button>
                  </>
                )}

                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteCartImage(selectedCart.id, selectedCart.cart_images![currentImageIndex])}
                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 shadow-lg"
                >
                  <Icon icon="mdi:delete" width="20" />
                </button>
              </div>

              {/* Thumbnail Navigation */}
              {selectedCart.cart_images.length > 1 && (
                <div className="flex gap-2 justify-center mb-4 overflow-x-auto pb-2">
                  {selectedCart.cart_images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                        idx === currentImageIndex ? 'border-blue-600' : 'border-gray-300'
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Image Counter */}
              <div className="text-center text-sm text-gray-600 mb-4">
                Image {currentImageIndex + 1} of {selectedCart.cart_images.length}
              </div>

              {/* Add More Images */}
              {selectedCart.cart_images.length < 5 && (
                <label className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-center cursor-pointer flex items-center justify-center gap-2">
                  <Icon icon="mdi:plus" width="20" />
                  Add More Images ({5 - selectedCart.cart_images.length} remaining)
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) {
                        handleCartImagesUpload(selectedCart.id, e.target.files);
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;